import { useCallback } from "react";
import { useMutation, useQuery } from "react-query";

import type { OtpChannel } from "@jti/fe-common";

import { MUTATION_KEYS, QUERY_KEYS } from "../../../constants/queryKeys";
import { CasProfileApi } from "../../../services";
import { getUser } from "../../../utils";

import type { UpdatePersonalDetailsData, UseProfileOptions, UseProfileReturnType } from "./types";

export const useProfile = ({ queryClient, onLogout }: UseProfileOptions): UseProfileReturnType => {
  const casProfileApi = CasProfileApi.init();
  const { email: userEmail = "", uuid: userUuid = "" } = getUser() || {};

  const { data, isLoading: isLoadingData } = useQuery(QUERY_KEYS.profile, () => casProfileApi.getProfile(), {
    staleTime: Infinity,
    cacheTime: Infinity,
    retry: 3,
    retryDelay: 5000,
  });

  const callLogout = useMutation(MUTATION_KEYS.logout, () => casProfileApi.logout(), {
    onSuccess: () => {
      onLogout?.(userUuid);
    },
    onError: (error) => {
      if ((error as { statusCode?: number }).statusCode === 403) {
        onLogout?.(userUuid);
      }
    },
  });

  const callUpdatePersonalData = useMutation(
    MUTATION_KEYS.updatePersonalData,
    (updateData: UpdatePersonalDetailsData) =>
      casProfileApi.updatePersonalData(
        updateData.firstname,
        updateData.lastname,
        updateData.date_of_birth,
        updateData.gender
      ),
    {
      onSuccess: async (response, { onSuccessOverride }) => {
        if (onSuccessOverride) {
          await onSuccessOverride(response);
          return;
        }

        await queryClient.invalidateQueries(QUERY_KEYS.profile);
      },
    }
  );

  const callChangePassword = useMutation(
    MUTATION_KEYS.changePassword,
    ({
      newPassword,
      currentPassword,
      confirmPassword,
    }: {
      newPassword: string;
      currentPassword: string;
      confirmPassword: string;
    }) => casProfileApi.changePassword(userUuid, userEmail, newPassword, currentPassword, confirmPassword)
  );

  const callUpdateEmail = useMutation(MUTATION_KEYS.updateEmail, (email: string) => casProfileApi.updateEmail(email), {
    onSuccess: () => {
      queryClient.invalidateQueries(QUERY_KEYS.profile);
    },
  });

  const callUpdatePhone = useMutation(
    MUTATION_KEYS.updatePhone,
    ({ phone, pvs, avp }: { phone: string; pvs?: string; avp?: string }) => casProfileApi.updatePhone(phone, pvs, avp),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QUERY_KEYS.profile);
      },
    }
  );

  const callSendOTC = useMutation(
    MUTATION_KEYS.sendOTC,
    ({ phone, otpChannel, template }: { phone: string; otpChannel: OtpChannel; template?: string }) =>
      casProfileApi.sendOTC(phone, otpChannel, template)
  );

  const callCheckPhone = useMutation(MUTATION_KEYS.checkPhone, (phone: string) => casProfileApi.verifiedPhone(phone));

  const callUpdateAgeVerification = useMutation(
    MUTATION_KEYS.updateAgeVerification,
    (avpUuid: string) => casProfileApi.updateAgeVerification(avpUuid),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QUERY_KEYS.profile);
      },
    }
  );

  const logout = useCallback(async () => {
    try {
      await callLogout.mutateAsync();
      return true;
    } catch (error) {
      console.error("Logout error:", error);
      return false;
    }
  }, [callLogout]);

  const changePassword = useCallback(
    async (newPassword: string, currentPassword: string, confirmPassword: string) =>
      await callChangePassword.mutateAsync({ newPassword, currentPassword, confirmPassword }),
    [callChangePassword]
  );

  const updatePersonalData = useCallback(
    async (updateData: UpdatePersonalDetailsData) => await callUpdatePersonalData.mutateAsync(updateData),
    [callUpdatePersonalData]
  );

  const updateEmail = useCallback(async (email: string) => await callUpdateEmail.mutateAsync(email), [callUpdateEmail]);

  const updateAgeVerification = useCallback(
    async (avpUuid: string) => await callUpdateAgeVerification.mutateAsync(avpUuid),
    [callUpdateAgeVerification]
  );

  const updatePhone = useCallback(
    async (phone: string, pvs?: string, avp?: string) => await callUpdatePhone.mutateAsync({ phone, pvs, avp }),
    [callUpdatePhone]
  );

  const sendOTC = useCallback(
    async (phone: string, otpChannel: OtpChannel, otpTemplateName?: string) =>
      await callSendOTC.mutateAsync({ phone, otpChannel, template: otpTemplateName }),
    [callSendOTC]
  );

  const checkPhone = useCallback(async (phone: string) => await callCheckPhone.mutateAsync(phone), [callCheckPhone]);

  return {
    isLoadingData,
    userUuid,
    data,
    logout,
    changePassword,
    updateEmail,
    updatePhone,
    sendOTC,
    checkPhone,
    updateAgeVerification,
    updatePersonalData,
  };
};
